// Identity frames remain the fast path; reconnect/wake reads only the viewer's own saved PC positions.
import type { WSMessage } from './types';
import { encounterCreatureField } from '../shared/array-types';
import { withAsyncDeadline } from '../shared/async-deadline';
import { recoverActiveViewerCasterAfterEncounterDeparture } from './caster-update';
import { beginPlayerFollow, failPlayerFollow, playerFollowReason, rememberPlayerFollowDestination, playerFollowOwnedByParent } from './player-follow';

type WSPlayerEncounterTransfer = Extract<WSMessage, { type: 'player_encounter_transfer' }>;
async function handlePlayerEncounterTransfer(message: WSPlayerEncounterTransfer): Promise<void> {
    const viewer = String(window.FateTide?.user?.id || '').trim().toUpperCase();
    if (window.FateTide?.user?.isGM || !checkIfValidUUID(viewer) || String(message.user_id).toUpperCase() !== viewer) {
        playerFollowReason('player-follow:identity-ignored', message.caster_id);
        return;
    }
    // Empty source is first placement; it has no source-roster signal.
    if (!checkIfValidUUID(message.caster_id) || !checkIfValidUUID(message.encounter_id)
        || message.from_encounter_id && !checkIfValidUUID(message.from_encounter_id)
        || message.from_encounter_id.toUpperCase() === message.encounter_id.toUpperCase()) {
        playerFollowReason('player-follow:invalid-transfer', message.caster_id);
        return;
    }
    await recoverActiveViewerCasterAfterEncounterDeparture(message.from_encounter_id, message.caster_id, message.encounter_id);
}

let catchup: Promise<void> | undefined;
const knownRooms = new Map<string, string>();
export function catchUpPlayerEncounter(source: 'reconnect' | 'wake'): Promise<void> {
    if (playerFollowOwnedByParent() || window.FateTide?.user?.isGM || !checkIfValidUUID(window.FateTide?.user?.id ?? '')) return Promise.resolve();
    if (catchup) return catchup;
    const campaignId = String(window.FateTide?.campaign?.id ?? '').trim();
    if (!checkIfValidUUID(campaignId)) return Promise.resolve();
    const active = String(window.FateTide?.activeEncounterId || window.ftGetActiveEncounterId?.() || '');
    const viewerCaster = String(window.FateTide?.activeViewerCasterId ?? '').toUpperCase();
    // Only this viewer's known PC keys; never scan/copy the held encounter or other casters.
    const before = new Map<string, string>();
    for (const id of new Set([...knownRooms.keys(), viewerCaster].filter(Boolean))) {
        const row = window.FateTide?.casterCache?.get(id);
        const heldRoom = row ? String(encounterCreatureField(row.encounter_creatures, 'encounter_id') ?? '') : undefined;
        before.set(id, heldRoom ?? knownRooms.get(id) ?? active);
    }
    const run = (async () => {
        let reason = 'player-follow:catchup-read-failed';
        for (let attempt = 0; attempt < 3; attempt++) {
            try {
                const payload = await withAsyncDeadline(async signal => {
                    const response = await fetch(`${BASE_URL}options/api/player_encounter_membership_inc.php?campaign_id=${encodeURIComponent(campaignId)}`, { credentials: 'same-origin', signal });
                    if (!response.ok) throw new Error(`membership:${response.status}`);
                    return await response.json() as { members: Array<{ caster_id: string; encounter_id: string | null; encounter_name?: string | null }> };
                }, 15000);
                if (!Array.isArray(payload.members)) throw new Error('membership:shape');
                for (const row of payload.members) {
                    const id = row.caster_id, destination = String(row.encounter_id ?? '');
                    if (!checkIfValidUUID(id)) continue;
                    rememberPlayerFollowDestination(destination, row.encounter_name);
                    const previous = before.get(id.toUpperCase());
                    knownRooms.set(id.toUpperCase(), destination);
                    if (!checkIfValidUUID(destination) || previous === undefined) continue;
                    if (previous.toUpperCase() === destination.toUpperCase()) continue;
                    await recoverActiveViewerCasterAfterEncounterDeparture(previous, id, destination);
                }
                return;
            } catch (error) {
                reason = error instanceof Error && error.name === 'TimeoutError'
                    ? 'player-follow:catchup-timeout' : 'player-follow:catchup-read-failed';
                playerFollowReason(reason);
                if (attempt < 2) await new Promise<void>(resolve => setTimeout(resolve, 750));
            }
        }
        const feedback = beginPlayerFollow(viewerCaster || source, '', () => { void catchUpPlayerEncounter(source); });
        failPlayerFollow(feedback, reason);
    })();
    catchup = run.finally(() => { catchup = undefined; });
    return catchup;
}

let mounted = false;
export function mountPlayerEncounterCatchup(): void {
    if (mounted || playerFollowOwnedByParent()) return;
    mounted = true;
    window.addEventListener('ft:ws-channel-subscribed', event => {
        const ack = (event as CustomEvent<{ channel?: string; status?: string; recovering?: boolean }>).detail;
        if (ack?.status === 'subscribed'
            && String(ack.channel).toUpperCase() === `USER:${window.FateTide?.user?.id ?? ''}`.toUpperCase()) void catchUpPlayerEncounter('reconnect');
    });
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void catchUpPlayerEncounter('wake');
    });
    document.addEventListener('resume', () => { void catchUpPlayerEncounter('wake'); });
    window.addEventListener('pageshow', event => { if ((event as PageTransitionEvent).persisted) void catchUpPlayerEncounter('wake'); });
}
export { handlePlayerEncounterTransfer };
